import { isNetworkError } from "@/lib/api/network-error";

/** Codes rendus par nginx quand l'API est absente — jamais un verdict métier. */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * Code HTTP d'une erreur axios, `undefined` si aucune réponse n'est revenue.
 *
 * Sert à ne pas confondre trois situations que le repli générique amalgame :
 * la ressource n'existe pas (404), l'accès est refusé (403), ou le serveur
 * n'a rien dit du tout. Annoncer « introuvable » sur une coupure réseau fait
 * croire qu'un acte clinique a été supprimé.
 */
export function httpStatus(err: unknown): number | undefined {
  const status = (err as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === "number" ? status : undefined;
}

/** Premier message texte d'une valeur d'erreur DRF, quelle que soit sa forme :
 *  `"msg"`, `["msg"]`, `{ champ: ["msg"] }` (objet imbriqué, ex. constantes
 *  vitales) ou `[{ non_field_errors: ["msg"] }]` (liste imbriquée, ex.
 *  diagnostics). Sans ce parcours, une erreur imbriquée tombait sur le repli
 *  générique « Échec de l'enregistrement. » sans dire ce qui bloque. */
function firstMessage(value: unknown, depth = 0): string | null {
  if (depth > 4 || value == null) return null;
  if (typeof value === "string") return value.trim() || null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstMessage(item, depth + 1);
      if (found) return found;
    }
    return null;
  }
  if (typeof value === "object") {
    for (const item of Object.values(value as Record<string, unknown>)) {
      const found = firstMessage(item, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

/** Clés DRF qui ne désignent pas un champ : le message se suffit. */
const NON_FIELD_KEYS = new Set(["detail", "non_field_errors"]);

/**
 * Extrait un message lisible d'une erreur API (axios + DRF).
 * DRF renvoie soit `{ detail: "..." }`, soit `{ champ: ["message", ...] }`.
 *
 * `labels` (clé API → libellé français) préfixe le message du nom du champ en
 * cause (« Praticien : le praticien doit être membre de la clinique. ») : un
 * message brut sans le champ, ou une erreur sur un champ masqué du formulaire,
 * ne dit pas à l'utilisateur quoi corriger.
 */
export function apiErrorMessage(
  err: unknown,
  fallback: string,
  labels?: Record<string, string>,
): string {
  // Serveur injoignable ou délai dépassé : aucun message serveur à extraire, et
  // le repli (« Échec de l'enregistrement. ») laisse croire à un refus métier
  // alors que la saisie est intacte et l'opération simplement à refaire.
  if (isNetworkError(err)) {
    return "Connexion au serveur perdue. Vérifiez le réseau, puis réessayez — votre saisie est conservée.";
  }

  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;

  // Réponse HTML brute (page 500 Django, page nginx…) : illisible dans un
  // toast — on affiche le message de repli.
  if (typeof data === "string" && data.trim() && !data.trimStart().startsWith("<")) {
    return data;
  }

  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (typeof obj.detail === "string") return obj.detail;
    for (const [key, value] of Object.entries(obj)) {
      const msg = firstMessage(value);
      if (!msg) continue;
      const label = labels?.[key];
      if (label && !NON_FIELD_KEYS.has(key)) {
        return `${label} : ${msg.charAt(0).toLowerCase()}${msg.slice(1)}`;
      }
      return msg;
    }
  }

  const status = httpStatus(err);

  // Passerelle (502/503/504) : l'API est momentanément absente — presque
  // toujours une mise à jour en cours, parfois un redémarrage. Ce n'est PAS
  // une panne à signaler : elle se rétablit seule en quelques dizaines de
  // secondes, et le rejeu de `client.ts` a déjà couvert les cas plus brefs.
  //
  // Le message générique 5xx envoyait appeler le support pour une indisponibilité
  // ordinaire, et laissait croire qu'il fallait faire quelque chose. Les
  // créations de patients, rendez-vous et consultations partent d'elles-mêmes en
  // file (cf. offline-mutations) ; ce message-ci s'adresse au reste — un
  // encaissement, une vente, une modification — qu'il faut effectivement
  // resoumettre, mais sans rien ressaisir.
  if (status !== undefined && GATEWAY_STATUSES.has(status)) {
    return "Mise à jour du serveur en cours. Votre saisie est conservée — réessayez dans quelques instants.";
  }

  // Panne serveur (5xx) : la réponse est une page HTML sans message exploitable.
  // Le repli métier (« Échec de l'enregistrement. ») ferait porter le doute sur
  // la saisie alors que le refus ne vient pas d'elle — l'utilisateur relit son
  // formulaire sans rien y trouver. On nomme la panne et on oriente vers le
  // support, seul capable d'agir.
  if (status !== undefined && status >= 500) {
    return `Erreur du serveur (${status}). L'enregistrement n'a pas abouti — réessayez, puis signalez-le au support si cela persiste.`;
  }

  return fallback;
}

type SetError = (field: string, error: { message: string }) => void;

/**
 * Mappe les erreurs de champ d'une réponse DRF 400 (`{ champ: "msg" | ["msg"] }`)
 * vers le formulaire (react-hook-form `setError`), pour afficher l'erreur SOUS le
 * champ concerné. Renvoie true si au moins une erreur de champ a été appliquée
 * (le formulaire peut alors éviter un toast générique).
 */
export function applyFieldErrors(err: unknown, setError: SetError, fields: string[]): boolean {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
  if (!data || typeof data !== "object") return false;
  const obj = data as Record<string, unknown>;
  let applied = false;
  for (const field of fields) {
    const value = obj[field];
    const msg = Array.isArray(value)
      ? typeof value[0] === "string"
        ? value[0]
        : ""
      : typeof value === "string"
        ? value
        : "";
    if (msg) {
      setError(field, { message: msg });
      applied = true;
    }
  }
  return applied;
}
